import * as React from 'react';
import { t } from 'i18next';

import { AppConfigurationStore } from '../stores/AppConfigurationStore';
import { ViewSettingsActions } from '../actions/ViewSettingsActions';
import { MessageBoxType, Overlay } from '../constants/Enums';
import { ActionHelper } from './ActionHelper';
import { ActionConstants } from '../constants/ActionConstants';
import { DispatcherBaseAction } from '../dispatcher/AppDispatcher';
import { Logging } from './Logging';
import { GlobalHelper } from './GlobalHelper';
import { ProjectHelper } from './ProjectHelper';
import { PconLoginStore } from '../stores/PconLoginStore';
import { ProjectSettingsStore, GroundType } from '../stores/ProjectSettingsStore';
import { VisibilityStore } from '../stores/VisibilityStore';
import { ProjectLoadingCancelError, ProjectLoadingError } from '../actions/ProjectManagerActions';
import { MessageBoxContent, MessageBoxContentProps } from '../components/MessageBoxContent';

import core = egr.wcf.core;
import utils = egr.wcf.utils;
import eaiws = egr.wcf.eaiws;

export interface FixedElementConfig {
    class: string;
    toggleClass: string;
}

export interface PromiseCallbacks<T, ResolveType = void> {
    promise: Promise<T>;
    resolve: (data: T) => ResolveType;
    reject: (error: Error) => void;
}

export abstract class UIHelper {
    public static showPrices(): boolean {
        return ProjectHelper.pricesPermitted() && PconLoginStore.pricesEnabled() && ProjectSettingsStore.getState().articleListPrices === 'show';
    }

    public static getUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
            /[xy]/g,
            (c: string): string => {
                // tslint:disable-next-line:no-bitwise
                const r: number = Math.random() * 16 | 0;
                // tslint:disable-next-line:no-bitwise
                const v: number = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            }
        );
    }

    public static getArticleHeader(articleProperties: eaiws.basket.ItemProperties, showArticleNumber: boolean, showSeriesName?: boolean): string {
        if (articleProperties.article == null) {
            return '';
        }

        const outputArray: Array<string> = [];
        if (showArticleNumber) {
            const articleNumber: string | undefined = articleProperties.article.useFinalArticleNumber ?
                articleProperties.article.finalArticleNumber : articleProperties.article.baseArticleNumber;

            if (utils.string.isNotNullOrEmpty(articleNumber)) {
                outputArray.push(articleNumber);
            }
        }

        if (!utils.string.isNullOrEmpty(articleProperties.article.shortText)) {
            outputArray.push(articleProperties.article.shortText);
        }

        if (showSeriesName && !utils.string.isNullOrEmpty(articleProperties.article.seriesName)) {
            outputArray.push(articleProperties.article.seriesName);
        }
        return outputArray.join(' | ');
    }

    public static multilineTextToArray(input: string, keepEmptyLines?: boolean): Array<string> {
        if (input == null || input === '') {
            return [];
        }

        const temp: string = input.replace('\r\n', '\n').replace('\r', '\n');
        return temp.split('\n').filter((value: string) => {
            if (keepEmptyLines) {
                return true;
            }

            return (value != null && value !== '');
        });
    }

    public static repositionFixedElements(scrollTop: number, breakpoint: number, baseElement: JQuery, elements: Array<FixedElementConfig>): void {
        if (!AppConfigurationStore.getState().iDevice) {
            if (scrollTop >= breakpoint) {
                elements.forEach((element) => {
                    baseElement.find(element.class).addClass(element.toggleClass);
                });
            } else {
                elements.forEach((element) => {
                    baseElement.find(element.class).removeClass(element.toggleClass);
                });
            }
        }
    }

    public static showMessage(message: string, onOk?: () => void): void {
        ViewSettingsActions.showMessage({
            wrap: false,
            element: React.createElement<MessageBoxContentProps>(
                MessageBoxContent,
                {
                    submitText: t('Ok'),
                    submitCallback: onOk
                },
                React.createElement(
                    'div',
                    {style: {overflow: 'hidden'}},
                    message
                )
            ),
            type: MessageBoxType.DEFAULT
        });
    }

    public static blurKeyboard(): Promise<void> {
        return new Promise<void>((resolve: () => void, reject: () => void) => {
            const field: HTMLInputElement = document.createElement('input');
            field.setAttribute('type', 'text');
            field.setAttribute(
                'style',
                'position:absolute; top: 0px; opacity: 0; -webkit-user-modify: read-write-plaintext-only; left:0px; border: 0; margin: 0; padding: 0;'
            );
            document.body.appendChild(field);
            field.focus();
            field.blur();
            setTimeout(
                () => {document.body.removeChild(field); },
                100
            );
            resolve();
        });
    }

    public static toDateString(inputDate: Date): string {
        const result: string = inputDate.toLocaleDateString();
        /* Workaround for Microsoft Edge - remove Left-To-Right-Mark */
        return result.replace(/&lrm;|\u200E/gi, '');
    }

    public static runAnimation(element: Element, maxDuration: number = 200): Promise<void> {
        // no detection if an animation was already started before adding "active-state" class
        return new Promise<void>(
            (resolve: () => void, reject: () => void) => {
                let runningTimeout: number;
                let executionTimeout: number;
                let animationEnded: () => void;

                const animationStarted: () => void = () => {
                    executionTimeout = window.setTimeout(animationEnded, maxDuration);
                    clearTimeout(runningTimeout);
                };

                animationEnded = () => {
                    clearTimeout(executionTimeout);
                    clearTimeout(runningTimeout);
                    element.removeEventListener('animationstart', animationStarted);
                    element.removeEventListener('animationend', animationEnded);
                    jQuery(element).removeClass('active-state');
                    resolve();
                };

                runningTimeout = window.setTimeout(animationEnded, 200);

                element.addEventListener('animationend', animationEnded);
                element.addEventListener('animationstart', animationStarted);

                jQuery(element).addClass('active-state');
            }
        );
    }

    public static middleEllipsis(text: string, maxChars: number, replacement: string = ' ... '): string {
        let cutLength: number;

        if (replacement.length / 2 === Math.floor(replacement.length / 2)) {
            cutLength = replacement.length;
        } else {
            cutLength = replacement.length + 1;
        }

        if (maxChars <= cutLength) {
            return '';
        }

        const remainingLength: number = Math.floor((maxChars - cutLength) / 2);

        if (text && text.length > maxChars) {
            if (remainingLength > 0) {
                return text.substring(0, remainingLength) + replacement + text.substring(text.length - remainingLength, text.length);
            }
            return text.substring(0, maxChars - cutLength) + replacement;
        }

        return text;
    }

    public static customScrollbarEnabled(): boolean {
        return (
            !AppConfigurationStore.getState().inApp
        );
    }

    public static updateScrollbar(
        pElement: JSX.Element | Element, scrollSelector: string = '.scrollbar-wcf', scrollTopPosition: number | null = null
    ): JQuery | undefined {
        if (pElement == null) {
            return undefined;
        }

        let scrollDiv: JQuery = jQuery(pElement);
        if (!scrollDiv.hasClass('scrollbar-wcf')) {
            scrollDiv = jQuery(pElement).find('.scrollbar-wcf');
        }

        if (scrollDiv && scrollDiv.length && UIHelper.customScrollbarEnabled() && jQuery(pElement).find('scroll-wrapper').length === 0) {
            scrollDiv.scrollbar({
                ignoreOverlay: true,
                ignoreMobile: true
            });
        }

        // if $(pElement).find(".scroll-content") doesnt return empty array the custom scrollbar was added and so we must scroll the new "scroll-content"
        const scrollContainer: JQuery =
            jQuery(pElement).find('.scroll-content').length ?
                jQuery(pElement).find('.scroll-content') :
                jQuery(pElement).find(scrollSelector);
        if (
            scrollTopPosition != null &&
            scrollContainer.scrollTop() != null &&
            scrollContainer.scrollTop() - 1 !== scrollTopPosition &&
            scrollContainer.scrollTop() !== scrollTopPosition &&
            scrollContainer.scrollTop() + 1 !== scrollTopPosition
        ) {
            scrollContainer.scrollTop(scrollTopPosition).scroll();
        }
        return scrollContainer;
    }

    /* Use:
        "onClick={UIHelper.stopEvent}" to stop propagation to parent-components
        or "onClick={foo}", "foo(event) { UIHelper.stopEvent(event); doYourStuff(); }"
        */
    // tslint:disable-next-line:no-any
    public static stopEvent(event: any): void {
        if (event) {
            if (event.stopPropagation) {
                event.stopPropagation();
            }
            if (event.nativeEvent) {
                event.nativeEvent.stopImmediatePropagation();
            }
            if (event.preventDefault) {
                event.preventDefault();
            }
        }
    }

    public static timeout(delay: number = 0): Promise<void> {
        return new Promise<void>((resolve: () => void, reject: (error: Error) => void): void => {
            setTimeout(resolve, delay);
        });
    }

    public static rejectAfter<T = void>(callback: () => Promise<T>, timeout: number, error: Error | null = null): Promise<T> {
        return new Promise<T>((resolve: (data: T) => void, reject: (error: Error) => void): void => {
            const timeoutHandle: number = window.setTimeout(
                (): void => {
                    reject(error != null ? error : new Error('Promise timeout reached'));
                },
                timeout
            );

            setTimeout(
                async (): Promise<void> => {
                    const data: T = await callback();

                    clearTimeout(timeoutHandle);

                    resolve(data);
                },
                0
            );
        });
    }

    public static waitUntilTrue(test: () => boolean, intervalTimeout: number = 150, rejectTimeout: number = 2000): Promise<void> {
        const error: Error = Logging.getError('Timeout reached!');
        return new Promise<void>((resolve: () => void, reject: (error: Error) => void): void => {
            const timeoutHandle: number = window.setTimeout(
                () => {
                    reject(error);
                },
                rejectTimeout
            );
            const intervalHandle: number = window.setInterval(
                () => {
                    if (test()) {
                        clearTimeout(timeoutHandle);
                        clearInterval(intervalHandle);
                        resolve();
                    }
                },
                intervalTimeout
            );
        });
    }

    public static async waitUntilOverlayChangesInto(wanted: Overlay): Promise<void> {
        if (VisibilityStore.getState().overlay !== wanted) {
            await ActionHelper.waitFor({
                actionTypes: [ActionConstants.SET_OVERLAY],
                condition: (action: DispatcherBaseAction) => {
                    switch (action.actionType) {
                        case ActionConstants.SET_OVERLAY:
                            return (action.value && action.value.overlay != null && action.value.overlay === wanted);
                        default:
                            return false;
                    }
                }
            });
        }
    }

    public static waitForLockVisibility(visible: boolean = true, ignoreReject: boolean = true): Promise<void> {
        return UIHelper.waitUntilTrue((): boolean => {
            return (jQuery('.main-lock:visible').length === 1) === visible;
        }).catch((error: Error): Promise<void> => {
            if (ignoreReject === true) {
                return Promise.resolve();
            }
            return Promise.reject<void>(error);
        });
    }

    public static insertBackgroundFrame(url: string, removeAfter: number | null = null): () => void {
        const iframe: HTMLIFrameElement = document.createElement('iframe') as HTMLIFrameElement;
        iframe.frameBorder = '0px';
        iframe.width = '0px';
        iframe.height = '0px';
        iframe.src = url;

        document.body.appendChild(iframe);
        const timeout: number | null = null;
        const removeFunc: () => void = () => {
            if (timeout != null) {
                window.clearTimeout(timeout);
            }
            document.body.removeChild(iframe);
        };

        if (removeAfter != null) {
            setTimeout(removeFunc, removeAfter);
        }

        return removeFunc;
    }

    public static errorDialog(message: string, submitText?: string, rejectError?: Error): Promise<void> {
        if (submitText == null) {
            submitText = t('Close');
        }

        return UIHelper.dialog(message, submitText, undefined, rejectError);
    }

    public static confirmDialog(message: string, submitText?: string, cancelText?: string, rejectError?: Error): Promise<void> {
        if (submitText == null) {
            submitText = t('OK');
        }

        if (cancelText == null) {
            cancelText = t('Cancel');
        }

        return UIHelper.dialog(message, submitText, cancelText, rejectError);
    }

    public static dialog(
        message: string | JSX.Element, submitText?: string, cancelText?: string, rejectError?: Error
    ): Promise<void> {
        return new Promise<void>((resolve: () => void, reject: (error?: Error) => void): void => {
            ViewSettingsActions.showMessage({
                wrap: false,
                element: React.createElement(
                    MessageBoxContent,
                    {
                        key: Date.now(),
                        submitText: submitText,
                        submitCallback: resolve,
                        cancelText: cancelText,
                        cancelCallback: (): void => {
                            reject(rejectError);
                        }
                    },
                    message
                ),
                type: MessageBoxType.DEFAULT
            });
        });
    }

    public static async ignoreReject(promise: Promise<void>, logError: boolean = true): Promise<void> {
        try {
            await promise;
        } catch (error) {
            if (error != null && logError) {
                console.error(error);
            }
        }
    }

    public static getPromiseCallbacks<T>(): PromiseCallbacks<T> {
        let resolveFunc: (data: T) => void;
        let rejectFunc: (error: Error) => void;
        const promise: Promise<T> = new Promise<T>((resolve: (data: T) => void, reject: (error: Error) => void) => {
            resolveFunc = resolve;
            rejectFunc = reject;
        });

        return {
            promise: promise,
            resolve: resolveFunc!,
            reject: rejectFunc!
        };
    }

    public static updateFloor(): void {
        if (ProjectSettingsStore.getFloorPlanVisibility()) {
            GlobalHelper.grid.setEnabled(false);
            GlobalHelper.floorPlane.setEnabled(false);
            GlobalHelper.setFloorPlaneColor(ProjectSettingsStore.getState().groundColor);
            GlobalHelper.floorPlan.enabled = true;
        } else {
            const value: GroundType = ProjectSettingsStore.getState().groundType || 'solid';
            GlobalHelper.grid.setEnabled(value === 'grid');
            GlobalHelper.floorPlane.setEnabled(value === 'solid');
            // GlobalHelper.setFloorPlaneColor(ProjectSettingsStore.getState().groundColor);
            GlobalHelper.floorPlan.enabled = false;
            if (value === 'tiledfloor') {
                const tScene: BABYLON.Scene = GlobalHelper.coreApp.model.scene;
                const tMat: core.mdl.PbrMaterial = new core.mdl.PbrMaterial('', GlobalHelper.coreApp.model.materials);
                tMat.metallic = .3;
                tMat.roughness = .6;
                tMat.backFaceCulling = true;

                const tScaling: number = 50 / (1 / 2.5);

                const tAlbedoTexture: BABYLON.Texture = new BABYLON.Texture('assets/fliesen_hell.png', tScene);
                tAlbedoTexture.uScale = tScaling;
                tAlbedoTexture.vScale = tScaling;
                tMat.albedoTexture = tAlbedoTexture;

                const tBumpTexture: BABYLON.Texture = new BABYLON.Texture('assets/fliesen_normal.png', tScene);
                tBumpTexture.uScale = tScaling;
                tBumpTexture.vScale = tScaling;
                tMat.bumpTexture = tBumpTexture;

                const tReflectivityTexture: BABYLON.Texture = new BABYLON.Texture('assets/fliesen_spec.png', tScene);
                tReflectivityTexture.uScale = tScaling;
                tReflectivityTexture.vScale = tScaling;
                tMat.reflectivityTexture = tReflectivityTexture;

                GlobalHelper.floorPlane.setEnabled(true);
                GlobalHelper.floorPlane.material = tMat;
            } else if (value === 'woodenfloor') {
                const tScene: BABYLON.Scene = GlobalHelper.coreApp.model.scene;
                const tMat: core.mdl.PbrMaterial = new core.mdl.PbrMaterial('', GlobalHelper.coreApp.model.materials);
                tMat.backFaceCulling = true;
                tMat.metallic = .1;
                tMat.roughness = .5;

                const tScaling: number = 50 / (1 / 1.2);

                const tAlbedoTexture: BABYLON.Texture = new BABYLON.Texture('assets/holzboden.jpg', tScene);
                tAlbedoTexture.uScale = tScaling;
                tAlbedoTexture.vScale = tScaling;
                tMat.albedoTexture = tAlbedoTexture;

                GlobalHelper.floorPlane.setEnabled(true);
                GlobalHelper.floorPlane.material = tMat;
            } else {
                const tMat: core.mdl.PbrMaterial = new core.mdl.PbrMaterial('', GlobalHelper.coreApp.model.materials);
                tMat.metallic = 0;
                tMat.roughness = 0.5;
                GlobalHelper.floorPlane.material = tMat;
                GlobalHelper.setFloorPlaneColor(ProjectSettingsStore.getState().groundColor);
            }
        }
        GlobalHelper.coreApp.viewer.requestRenderFrame();
    }

    public static handleProjectLoadingError(error: Error): void {
        if (error instanceof ProjectLoadingCancelError) {
            // user clicked cancel -> ignore
            return;
        }
        if (error instanceof ProjectLoadingError) {
            UIHelper.errorDialog(error.message);
        } else {
            throw error;
        }
    }

    public static loadImage(src: string | null): Promise<HTMLImageElement | null> {
        if (utils.string.isNullOrEmpty(src)) {
            return Promise.resolve(null);
        }

        return new Promise<HTMLImageElement>((resolve: (image: HTMLImageElement) => void, reject: (error: Error) => void) => {
            const image: HTMLImageElement = new Image();

            image.onload = (): void => {
                resolve(image);
            };

            // image.onerror = (errorEvent: ErrorEvent): void => {
            //     reject(errorEvent.error);
            // };
            image.onerror = (): void => {
                reject(utils.newError(utils.ERR_FAILED, 'Failed to load image: ' + src));
            };

            image.src = src;
        });
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/UIHelper.ts